import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/user-groups/UserGroupsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { UserGroupsFilter } from "./user-groups-filter/UserGroupsFilter";
import { UserGroupsTable } from "./user-groups-table/UserGroupsTable";
import { useUserGroupsUIContext } from "./UserGroupsUIContext";

export function UserGroupsCard() {
  // User Groups UI Context
  const userGroupsUIContext = useUserGroupsUIContext();
  const userGroupsUIProps = useMemo(() => {
    return {
      ids: userGroupsUIContext.ids,
      setIds: userGroupsUIContext.setIds,
      queryParams: userGroupsUIContext.queryParams,
      openNewUserGroupPage: userGroupsUIContext.openNewUserGroupPage,
    };
  }, [userGroupsUIContext]);

  // Getting curret state of User Group list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.userGroups }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // User Groups Redux state
  const dispatch = useDispatch();
   
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Kullanıcı Grubu Listesini Excele Aktarır */
  const exportUserGroupsToExcel = () => {
    dispatch(actions.getAllUserGroups(userGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 45];
      const columns = ["Adı", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.name, entity.definition]));
      const filename = "Kullanıcı Grubu Listesi"
      exportToExcel({ columns, rows, widths, filename });
    });
  }

  /** Belli Bir Kullanıcı Grubu Listesini PDF e Aktarır */
  const exportUserGroupsToPDF = () => {
    dispatch(actions.getAllUserGroups(userGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Açıklaması",
          rows: entities.map(entity => entity.definition)
        }
      ];
  
      const filename = "Kullanıcı Grubu Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Kullanıcı Grubu Listesini Yazdırma Penceresini Açar */
  const printUserGroups = () => {
    dispatch(actions.getAllUserGroups(userGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Kullanıcı Grubu Listesi";
      const properties = ["Adı", "Açıklaması"];

      entities.forEach(entity => {
        data.push({
          "Adı": entity.name,
          "Açıklaması": entity.definition ?? ""
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Kullanıcı Grubu Listesini Panoya Kopyalar */
  const copyUserGroups = () => {
    dispatch(actions.getAllUserGroups(userGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Kullanıcı Grubu Listesi";
      const columns = ["Adı", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.name, entity.definition ?? ""]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Grubu Listesi Siliniyor */
  const deleteUserGroups = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteUserGroups(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        userGroupsUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getUserGroups(userGroupsUIProps.queryParams));      
      }, 2500);
    }
  };
  
  const title = <>Kullanıcı Grubu Listesi{userGroupsUIProps.ids.length > 0 ? 
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{userGroupsUIProps.ids.length}</span>]</> :
                   ""}</>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportUserGroupsToExcel}
                exportToPDF={exportUserGroupsToPDF}
                print={printUserGroups}
                copy={copyUserGroups}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Kullanıcı Grubu"
                method={userGroupsUIProps.openNewUserGroupPage}
              />
            }              
            {
              isDeletionAuthorized &&
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteUserGroups(userGroupsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <UserGroupsFilter />
        <UserGroupsTable 
          deleteUserGroups={deleteUserGroups}
        />
      </CardBody>
    </Card>
  );
}